import { AttendanceVerificationMethod } from "@clipboard-health/contract-backend-main";
import { useGetAttendanceVerification } from "@src/appV2/FacilityProfile/AttendanceVerificationMethods/api/useGetAttendanceVerification";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useMemo } from "react";

import { type ExtendedExternalIntegrationType, ExtendedExternalIntegrationTypes } from "../types";
import { useGetWorkplaceExternalIntegrations } from "./useGetWorkplaceExternalIntegrations";

interface UseGetWorkplaceEnabledConfigurationsParams {
  workplaceId: string;
}

export function useGetWorkplaceEnabledConfigurations(
  params: UseGetWorkplaceEnabledConfigurationsParams
): ExtendedExternalIntegrationType[] {
  const { workplaceId } = params;

  const { data: externalIntegrations } = useGetWorkplaceExternalIntegrations({ workplaceId });

  const { data: attendanceVerification } = useGetAttendanceVerification(workplaceId);

  const { enabled: touchpointIntegrationEnabled } = useCbhFlag(
    CbhFeatureFlag.TOUCHPOINT_INTEGRATION,
    {
      defaultValue: {
        enabled: false,
      },
    }
  );

  const isTimeclockComplianceEnabled = useMemo(() => {
    const methods = attendanceVerification?.data?.attributes?.methods ?? [];
    return methods.some(
      (method) =>
        [
          AttendanceVerificationMethod.AI_TAKE_A_VIDEO,
          AttendanceVerificationMethod.TAKE_A_VIDEO,
        ].includes(method.type) && method.isEnabled
    );
  }, [attendanceVerification]);

  return useMemo(() => {
    const types: ExtendedExternalIntegrationType[] = (externalIntegrations ?? [])
      .filter((integration) => integration.isEnabled)
      .map((integration) => integration.type);

    if (touchpointIntegrationEnabled) {
      types.push(ExtendedExternalIntegrationTypes.MESH);
    }

    if (
      isTimeclockComplianceEnabled &&
      !types.includes(ExtendedExternalIntegrationTypes.TIMECLOCK_COMPLIANCE)
    ) {
      types.push(ExtendedExternalIntegrationTypes.TIMECLOCK_COMPLIANCE);
    }

    return types;
  }, [externalIntegrations, touchpointIntegrationEnabled, isTimeclockComplianceEnabled]);
}
